import gql from 'graphql-tag';

import {Permissions} from 'auth/PermissionConstants';
import {User} from 'constants/UserConstants';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import {SchemaValidationErrorRecord} from 'schemas/SchemaValidationConstants';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';
import {OngoingTaskResponse} from 'util/TaskRunner';
import {Optional} from 'util/Types';

import {
  ReviewSummaryForReviewableQuery,
  ReviewSummaryForReviewableQueryVariables,
} from './__generated/ReviewSummaryForReviewableQuery';
import requireEsignature, {EsignFailureError} from './requireEsignature';
import {ESignatureInfo, Review, ReviewAction, ReviewChangeTimestamps} from './ReviewConstants';

export interface ReviewUpdatedResponse {
  review: Review;
  filePermissions: Permissions;
  snapshotTask: OngoingTaskResponse | null;
}

export function isReviewUpdatedResponse(response?: ReviewUpdatedResponse): response is ReviewUpdatedResponse {
  return response != null;
}

export function fetchAuditors(id: string): Promise<{auditors: Array<User>}> {
  return net.getJSONPromise('/1/api/reviews/actions/get-auditor-options', {id});
}

export function fetchReview(reviewId: string): Promise<Review> {
  return net.getJSONPromise(`/1/api/reviews/${reviewId}`);
}

export async function updateReview(
  reviewId: string,
  signatureInfo: Optional<ESignatureInfo>,
  data: {auditorHandles?: Array<string>}
): Promise<ReviewUpdatedResponse> {
  return await net.patchJSONPromise({
    url: `/1/api/reviews/${reviewId}`,
    data: {signatureInfo, ...data},
  });
}

/**
 * Validate the given reviewable's tags bending sending for review
 *
 * @param {String} id - the apiIdentifier of either an Entry or a StageEntry
 * @return {jqPromise} - promise that resolves with an array of error objects
 */
export function validateFileTags(id: string): Promise<Array<SchemaValidationErrorRecord>> {
  return net
    .postJSONPromise({
      url: '/1/api/reviews/actions/validate-file-tags',
      data: {id},
    })
    .then(({errors}) => errors);
}

export const bulkValidateFileTags = async (
  reviewableIds: Array<string>
): Promise<{[reviewableId: string]: Array<SchemaValidationErrorRecord>}> => {
  const {errorsByReviewableId} = await net.postJSONPromise({
    url: '/1/api/reviews:bulk-validate-file-tags',
    data: {reviewableIds},
  });
  return errorsByReviewableId;
};

export const bulkGetReviewChangeTimestamps = async (
  entryIds: Array<string>
): Promise<{[entryId: string]: ReviewChangeTimestamps}> => {
  const {reviewChangeTimestampsByEntryId} = await net.postJSONPromise({
    url: '/1/api/reviews:bulk-get-review-change-timestamps',
    data: {entryIds},
  });
  return reviewChangeTimestampsByEntryId;
};

export function bulkCreateReview(data: {
  action: ReviewAction;
  message: string | null;
  auditorHandles: Array<string>;
  reviewableIds: Array<string>;
}): Promise<{reviewUpdatedResponseByReviewableId: {[reviewableId: string]: ReviewUpdatedResponse}}> {
  return net.postJSONPromise({
    url: '/1/api/reviews:bulk-create',
    data,
  });
}

export const fetchAuditorsForWorkflow = async (workflowId: string): Promise<Array<User>> => {
  const {auditors} = await net.getJSONPromise('/1/api/reviews/workflow-auditor-options', {
    workflowId,
  });
  return auditors;
};

export async function createReview(
  signatureInfo: Optional<ESignatureInfo>,
  data: {action: ReviewAction; message: string | null; auditorHandles: Array<string>; fileApiId: string}
): Promise<ReviewUpdatedResponse> {
  const response = await net.postJSONPromise({
    url: '/1/api/reviews/',
    data: {signatureInfo, ...data},
  });
  // Update Apollo cache with newly updated review object, and keep GQL subscribed components up to date.
  await fetchReviewSummary(data.fileApiId);
  return response;
}

export async function createReviewChange(
  fileApiId: string,
  reviewId: string,
  signatureInfo: Optional<ESignatureInfo>,
  data: {action: ReviewAction; auditorHandles?: Array<string>; message?: string}
): Promise<ReviewUpdatedResponse> {
  const response = await net.postJSONPromise({
    url: `/1/api/reviews/${reviewId}:create-review-change`,
    data: {signatureInfo, ...data},
  });
  // Update Apollo cache with newly updated review object, and keep GQL subscribed components up to date.
  await fetchReviewSummary(fileApiId);
  return response;
}

export async function retryReviewSnapshot(
  fileApiId: string,
  reviewId: string
): Promise<ReviewUpdatedResponse> {
  const response = await net.postJSONPromise({
    url: `/1/api/reviews/${reviewId}:retry-review-snapshot`,
  });
  // Update Apollo cache with newly updated review object, and keep GQL subscribed components up to date.
  await fetchReviewSummary(fileApiId);
  return response;
}

// Specific to RLC Reviews only

export interface RLCStageReviewerData {
  stageId: string;
  reviewerUserId: string;
  isTeamReassignable?: boolean;
}

export interface SendForRLCReviewData {
  fileApiId: string;
  message: string;
  rlcStageReviewersData: Array<RLCStageReviewerData>;
}

export const REVIEW_SUMMARY_FOR_REVIEWABLE_QUERY = gql`
  query ReviewSummaryForReviewableQuery($reviewableId: StrictString!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    reviewable(id: $reviewableId) {
      ... on ReviewableInterface {
        id
        review {
          ...ReviewLifecycleReviewSummaryGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ReviewLifecycleReviewSummary').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function fetchReviewSummary(fileApiId: string): Promise<ReviewSummaryForReviewableQuery | null> {
  // We don't need to fetch this for stage entries, as they don't use GraphQL.
  if (APIUtil.isStageEntry(fileApiId)) {
    return Promise.resolve(null);
  }
  return doQuerySafe<ReviewSummaryForReviewableQuery, ReviewSummaryForReviewableQueryVariables>(
    {
      query: REVIEW_SUMMARY_FOR_REVIEWABLE_QUERY,
    },
    {
      variables: {reviewableId: fileApiId},
      fetchPolicy: 'network-only',
    }
  );
}

export async function getSignatureInfo(): Promise<ESignatureInfo | null> {
  let signatureInfo;
  try {
    signatureInfo = await requireEsignature();
  } catch (error) {
    if (error instanceof EsignFailureError) {
      notifications.error(error.message);
      throw new Error(error.message);
    }
    throw new Error('Failed to authenticate');
  }
  return signatureInfo;
}

export async function sendForRLCReview(data: SendForRLCReviewData): Promise<ReviewUpdatedResponse> {
  const signatureInfo = await getSignatureInfo();
  const response = await net.postJSONPromise({
    url: '/1/api/rlc-reviews/',
    data: {signatureInfo, ...data},
  });
  // Update Apollo cache with newly updated review object, and keep GQL subscribed components up to date.
  await fetchReviewSummary(data.fileApiId);
  return response;
}

export interface ReassignRLCReviewerDataParams {
  fileId: string;
  reviewLifecycleStageId: string;
  newUserId: string;
}

export async function reassignRLCReviewer(
  data: ReassignRLCReviewerDataParams
): Promise<ReviewUpdatedResponse> {
  const signatureInfo = await getSignatureInfo();
  const response = await net.postJSONPromise({
    url: '/1/api/update-reviewer/',
    data: {signatureInfo, ...data},
  });
  // Update Apollo cache with newly updated review object, and keep GQL subscribed components up to date.
  await fetchReviewSummary(data.fileId);
  return response;
}
